<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const formName = ref('meter');
  const formId = ref(null);
  const submitAndClose = ref(true);
  const container = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    submitAndClose: submitAndClose.value,
    container: container.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateMeter', { multiplier: 1 });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_container = async () => {
    return {
      self: $fn.value.urlFor('create_meter'),
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'submitAndClose' },
      { name: 'container' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      container: $$load_container,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      submitAndClose,
      container,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Meter') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_00jgl0t62_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_2mpn2khfd_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_itng6uopm_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.meter',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_b7oxp6ng3_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meters',
    };
  };
  const act_1040167445267876_rest_i11rpdclr_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };
  const act_2434514588044777_script_qlj2wiwwi_value_callable = async (
    $event
  ) => {
    return (
      setData(
        'item',
        item.value.clone({
          id: null,
          rev: null,
          serial_number: null,
          links: null,
          etag: null,
          locked: null,
        })
      ),
      setData('editor', item.value.editor())
    );
  };
  const act_2434514588044777_script_qlj2wiwwi_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2434514588044777_script_qlj2wiwwi_value_callable($event);
      },
    };
  };
  const act_2434514588044777_reloadSlickgrid_ye5wlqxkv_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meters',
    };
  };
  const act_2434514588044777_rest_vehbzefhu_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };

  const act_7315092382398562_closeModal_00jgl0t62 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_00jgl0t62_getActionArgs,
    });
  const inst_evh_7315092382398562_close_i2eqbr47p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_00jgl0t62,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_i2eqbr47p],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_8vctv70a3 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_h7mcpntk0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8vctv70a3,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_h7mcpntk0],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ts0s2hzx9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_qzrrv0s28 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ts0s2hzx9,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_qzrrv0s28],
    event: 'close',
  });
  const act_1040167445267876_closeModal_2mpn2khfd =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_2mpn2khfd_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_owd1l06bb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_2mpn2khfd,
      event: 'saved',
      displayName: 'closeModal',
    });
  const act_1040167445267876_navigate_itng6uopm =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_itng6uopm_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_o14vstnnq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_itng6uopm,
      event: 'saved',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_b7oxp6ng3 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_b7oxp6ng3_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_rzzne0m0a =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_b7oxp6ng3,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_rest_i11rpdclr = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_i11rpdclr_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_saved_owd1l06bb,
      inst_evh_1040167445267876_saved_o14vstnnq,
      inst_evh_1040167445267876_saved_rzzne0m0a,
    ],
  });
  const inst_evh_1040167445267876_submit_j8zwjw9dm =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_i11rpdclr,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_j8zwjw9dm],
    event: 'submit',
  });
  const act_2434514588044777_script_qlj2wiwwi = new actionTypes.ScriptAction({
    actionArgs: act_2434514588044777_script_qlj2wiwwi_getActionArgs,
  });
  const inst_evh_2434514588044777_saved_5kcjeujir =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_script_qlj2wiwwi,
      event: 'saved',
      displayName: 'script',
    });
  const act_2434514588044777_reloadSlickgrid_ye5wlqxkv =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2434514588044777_reloadSlickgrid_ye5wlqxkv_getActionArgs,
    });
  const inst_evh_2434514588044777_saved_xuprbauui =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_reloadSlickgrid_ye5wlqxkv,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_2434514588044777_rest_vehbzefhu = new actionTypes.RestAction({
    actionArgs: act_2434514588044777_rest_vehbzefhu_getActionArgs,
    displayName: 'create2',
    events: [
      inst_evh_2434514588044777_saved_5kcjeujir,
      inst_evh_2434514588044777_saved_xuprbauui,
    ],
  });
  const inst_evh_2434514588044777_click_6wkyi64y1 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_rest_vehbzefhu,
      event: 'click',
      displayName: 'create2',
    });
  const inst_evh_2434514588044777_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2434514588044777_click_6wkyi64y1],
    event: 'click',
  });
  const act_2167826738365259_closeComponent_f0hs74n8r =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2167826738365259_clickPreventStop_5qc2n377k =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeComponent_f0hs74n8r,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_5qc2n377k],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_i2eqbr47p,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_h7mcpntk0,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_qzrrv0s28,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_owd1l06bb,
    inst_evh_1040167445267876_saved_o14vstnnq,
    inst_evh_1040167445267876_saved_rzzne0m0a,
    inst_evh_1040167445267876_submit_j8zwjw9dm,
    create: inst_evh_1040167445267876_submit_j8zwjw9dm,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_saved_5kcjeujir,
    inst_evh_2434514588044777_saved_xuprbauui,
    inst_evh_2434514588044777_click_6wkyi64y1,
    create2: inst_evh_2434514588044777_click_6wkyi64y1,
    inst_evh_2434514588044777_click,
    inst_evh_2167826738365259_clickPreventStop_5qc2n377k,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_i2eqbr47p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_i2eqbr47p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_h7mcpntk0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_h7mcpntk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_qzrrv0s28 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_qzrrv0s28.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_owd1l06bb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_owd1l06bb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_o14vstnnq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_o14vstnnq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_rzzne0m0a = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_rzzne0m0a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_j8zwjw9dm = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_j8zwjw9dm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_saved_5kcjeujir = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_saved_5kcjeujir.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_saved_xuprbauui = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_saved_xuprbauui.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_click_6wkyi64y1 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_click_6wkyi64y1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_click = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_5qc2n377k = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_5qc2n377k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateMeter"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetMeterGeneralData :meter="item"> </FieldsetMeterGeneralData>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.create.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="
            pgettext('LetterRegisterEntry/Command', 'Save and create another')
          "
          :disabled="
            !forms[formName] ||
            !forms[formName].submittable ||
            (actions.create && actions.create.isRunning)
          "
          variant="light"
          icon="mdi:forward"
          class="me-2"
          @click="evh_2434514588044777_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
